import React, { useState } from 'react';
import { HelpCircle, Camera, Send, X, MessageSquare, Bug, Upload, Lightbulb, ImagePlus, FileText, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { useFloatingPanel } from '../hooks/useFloatingPanel';

interface TrelloErrorReportProps {
  error?: Error;
  errorInfo?: any;
  className?: string;
  alwaysVisible?: boolean;
  isOpen?: boolean;       // controle externo
  onClose?: () => void;   // controle externo
  shiftLeft?: boolean;    // desloca o modal para a esquerda quando o painel do Especialista em Propostas também está aberto
}

// Painel do Especialista em Propostas (ChatIAWidget.tsx): right:20px, bottom:86px, altura padrão 520px
const CHAT_PANEL_RIGHT = 20 + 380 + 16; // 20 (offset) + 380 (largura padrão) + 16 (gap) = 416px
const CHAT_PANEL_BOTTOM = 86;
const CHAT_PANEL_HEIGHT = 520;

interface TrelloConfig {
  apiKey: string;
  token: string;
  boardId: string;
  listId: string;
}

const MAX_ATTACHMENTS = 5;
const DOCUMENT_EXTENSIONS = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip';

interface ReportAttachment {
  dataUrl: string;
  name: string;
  isImage: boolean;
}

export const TrelloErrorReport: React.FC<TrelloErrorReportProps> = ({
  error,
  errorInfo,
  className = '',
  alwaysVisible = true,
  isOpen: controlledIsOpen,
  onClose,
  shiftLeft = false,
}) => {
  const controlled = controlledIsOpen !== undefined;
  const [isOpen, setIsOpen] = useState(false);
  const effectiveIsOpen = controlled ? (controlledIsOpen ?? false) : isOpen;
  const handleClose = controlled ? (onClose ?? (() => {})) : () => setIsOpen(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<ReportAttachment[]>([]);
  const [reportType, setReportType] = useState<'bug' | 'suggestion'>('bug');
  const [isDragOver, setIsDragOver] = useState(false);
  const {
    panelRef,
    width: panelWidth,
    height: panelHeight,
    dragOffset,
    hasMoved,
    handleTopResize,
    handleLeftResize,
    handleCornerResize,
    handleDragStart,
  } = useFloatingPanel({ isOpen: effectiveIsOpen, initialWidth: 384, initialHeight: CHAT_PANEL_HEIGHT, minWidth: 320, minHeight: 320 });

  const collectReportInfo = () => {
    const timestamp = new Date().toISOString();
    const url = window.location.href;
    const userAgent = navigator.userAgent;
    const userId = localStorage.getItem('userId') || 'anonymous';
    const screenSize = `${window.innerWidth}x${window.innerHeight}`;
    const referrer = document.referrer || 'Acesso direto';
    
    // Buscar dados completos do usuário do localStorage
    let userEmail = 'Não informado';
    let organizationId = 'Não informado';
    let organizationName = 'Não informado';
    let userRole = 'Não informado';
    let planType = 'Não informado';
    let licenseStatus = 'Não informado';
    
    try {
      const userStr = localStorage.getItem('prainte_user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        userEmail = userData.email || 'Não informado';
        organizationId = userData.organization_id?.toString() || 'Não informado';
        organizationName = userData.organization_name || 'Não informado';
        userRole = userData.user_role || userData.role || 'Não informado';
      }
    } catch (e) {
      console.error('Erro ao buscar dados do usuário:', e);
    }
    
    return {
      timestamp,
      url,
      userAgent,
      userId,
      userEmail,
      organizationId,
      organizationName,
      userRole,
      planType,
      licenseStatus,
      screenSize,
      referrer,
      errorMessage: error?.message || 'Nenhum erro específico',
      errorStack: error?.stack || '',
      errorInfo: errorInfo || {},
      description,
      reportType
    };
  };

  const captureScreenshot = async () => {
    // Verificar se o navegador suporta screen capture
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      toast.info('Seu navegador não suporta captura de tela. Use a opção de upload de imagem.');
      return;
    }
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const video = document.createElement('video');
      
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true
      });
      
      video.srcObject = stream;
      video.play();
      
      video.onloadedmetadata = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx?.drawImage(video, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            if (attachments.length >= MAX_ATTACHMENTS) {
              toast.error(`Máximo de ${MAX_ATTACHMENTS} anexos por relato.`);
              return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
              setAttachments(prev => [...prev, {
                dataUrl: reader.result as string,
                name: `screenshot-${prev.length + 1}.png`,
                isImage: true,
              }]);
              toast.success('Screenshot capturado com sucesso!');
            };
            reader.readAsDataURL(blob);
          }
        });
        stream.getTracks().forEach(track => track.stop());
      };
    } catch (err: any) {
      console.log('Screenshot não disponível:', err);
      
      // Tratar diferentes tipos de erro
      if (err.name === 'NotAllowedError') {
        toast.info('Permissão negada. Você pode usar a opção de upload de imagem.');
      } else if (err.name === 'NotSupportedError') {
        toast.info('Captura de tela não suportada. Use a opção de upload de imagem.');
      } else if (err.name === 'SecurityError') {
        toast.info('Restrições de segurança. Use a opção de upload de imagem.');
      } else {
        toast.info('Captura de tela não disponível. Use a opção de upload de imagem.');
      }
    }
  };

  const processFile = (file: File, kind: 'image' | 'document') => {
    if (kind === 'image' && !file.type.startsWith('image/')) {
      toast.error('Por favor, selecione um arquivo de imagem.');
      return;
    }

    // Verificar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`${kind === 'image' ? 'A imagem' : 'O documento'} deve ter no máximo 5MB.`);
      return;
    }

    if (attachments.length >= MAX_ATTACHMENTS) {
      toast.error(`Máximo de ${MAX_ATTACHMENTS} anexos por relato.`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setAttachments(prev => [...prev, {
        dataUrl: reader.result as string,
        name: file.name,
        isImage: file.type.startsWith('image/'),
      }]);
      toast.success(`${kind === 'image' ? 'Imagem' : 'Documento'} adicionado com sucesso!`);
    };
    reader.readAsDataURL(file);
  };

  const addFiles = (files: FileList | null, kind: 'image' | 'document') => {
    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) {
      toast.error(`Máximo de ${MAX_ATTACHMENTS} anexos por relato.`);
      return;
    }
    Array.from(files ?? []).slice(0, remaining).forEach(f => processFile(f, kind));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files, 'image');
    event.target.value = '';
  };

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files, 'document');
    event.target.value = '';
  };

  const handleImageDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    Array.from(event.dataTransfer.files ?? []).slice(0, MAX_ATTACHMENTS - attachments.length).forEach(
      f => processFile(f, f.type.startsWith('image/') ? 'image' : 'document')
    );
  };

  const handleImageDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleImageDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const uploadAttachment = async (cardId: string, dataUrl: string, filename: string, config: TrelloConfig) => {
    const blob = await (await fetch(dataUrl)).blob();
    const attachFormData = new FormData();
    attachFormData.append('file', blob, filename);
    attachFormData.append('key', config.apiKey);
    attachFormData.append('token', config.token);

    const attachResponse = await fetch(`https://api.trello.com/1/cards/${cardId}/attachments`, {
      method: 'POST',
      body: attachFormData
    });

    if (!attachResponse.ok) {
      const attachText = await attachResponse.text().catch(() => '');
      console.warn(`Falha ao anexar ${filename}:`, attachText);
      throw new Error(attachText || `Falha ao anexar ${filename}`);
    }
  };

  const submitToTrello = async () => {
    setIsSubmitting(true);
    
    try {
      const reportData = collectReportInfo();
      const config: TrelloConfig = {
        apiKey: import.meta.env.VITE_TRELLO_API_KEY || '',
        token: import.meta.env.VITE_TRELLO_TOKEN || '',
        boardId: import.meta.env.VITE_TRELLO_BOARD_ID || '',
        listId: import.meta.env.VITE_TRELLO_LIST_ID || ''
      };

      const missingFields: string[] = [];
      if (!config.apiKey) missingFields.push('VITE_TRELLO_API_KEY');
      if (!config.token) missingFields.push('VITE_TRELLO_TOKEN');
      if (!config.listId) missingFields.push('VITE_TRELLO_LIST_ID');
      if (missingFields.length > 0) {
        throw new Error(`Configuração do Trello incompleta: ${missingFields.join(', ')}`);
      }

      const getReportIcon = () => {
        switch (reportType) {
          case 'bug': return '🐛';
          case 'suggestion': return '💡';
          default: return '📝';
        }
      };

      const getReportTitle = () => {
        switch (reportType) {
          case 'bug': return 'Bug';
          case 'suggestion': return 'Sugestão';
          default: return 'Feedback';
        }
      };

      const titleExcerpt = description.length > 180 ? `${description.substring(0, 180).trim()}…` : description;
      const cardTitle = `${getReportIcon()} ${getReportTitle()}: ${titleExcerpt || 'Sem título'}`;
      
      const cardDescription = `
**Tipo de Report:** ${getReportTitle()}
**URL:** ${reportData.url}
**Referrer:** ${reportData.referrer}

---
**Usuário ID:** ${reportData.userId}
**Email:** ${reportData.userEmail}
**Role:** ${reportData.userRole}

---
**Organização ID:** ${reportData.organizationId}
**Organização:** ${reportData.organizationName}
**Plano:** ${reportData.planType}
**Status Licença:** ${reportData.licenseStatus}

---
**Data/Hora:** ${reportData.timestamp}
**Navegador:** ${reportData.userAgent}
**Resolução:** ${reportData.screenSize}

${error ? `**Erro:** ${reportData.errorMessage}` : ''}

**Descrição:**
${description || 'Nenhuma descrição fornecida'}

${error ? `**Stack Trace:**
\`\`\`
${reportData.errorStack}
\`\`\`` : ''}

**Informações adicionais:**
\`\`\`json
${JSON.stringify(error ? reportData.errorInfo : {}, null, 2)}
\`\`\`
      `.trim();

      const params = new URLSearchParams({
        name: cardTitle,
        desc: cardDescription,
        idList: config.listId,
        key: config.apiKey,
        token: config.token
      });

      const response = await fetch(`https://api.trello.com/1/cards?${params}`, {
        method: 'POST'
      });

      if (!response.ok) {
        const responseText = await response.text().catch(() => '');
        throw new Error(`Trello retornou ${response.status}: ${responseText || response.statusText}`);
      }

      const card = await response.json();

      if (attachments.length > 0) {
        const results = await Promise.allSettled(
          attachments.map(att => uploadAttachment(card.id, att.dataUrl, att.name, config))
        );
        const failedCount = results.filter(r => r.status === 'rejected').length;
        if (failedCount === 0) {
          toast.success('Obrigado pelo seu feedback! Nossa equipe vai analisar em breve.');
        } else if (failedCount < attachments.length) {
          toast.success(`Obrigado pelo seu feedback! (${failedCount} de ${attachments.length} anexos não puderam ser enviados, mas seu envio foi registrado.)`);
        } else {
          toast.success('Obrigado pelo seu feedback! (Não foi possível enviar os anexos, mas seu envio foi registrado.)');
        }
      } else {
        toast.success('Obrigado pelo seu feedback! Nossa equipe vai analisar em breve.');
      }

      setDescription('');
      setAttachments([]);
      handleClose();

      console.log('Cartão criado:', card);
      
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('Erro ao enviar para Trello:', err);
      toast.error(`Falha ao enviar report: ${message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Se não for controlado, não for alwaysVisible e não houver erro, não renderiza nada
  if (!controlled && !alwaysVisible && !error) {
    return null;
  }

  return (
    <div className={`fixed bottom-4 right-4 z-50 ${className}`}>
      {/* Botão flutuante — só renderizado quando não controlado externamente */}
      {!controlled && !effectiveIsOpen && (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            width: '48px',
            height: '48px',
            backgroundColor: '#3B82F6',
            color: 'white',
            borderRadius: '50%',
            padding: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            border: 'none',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2563EB';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#3B82F6';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title={error ? "Reportar erro" : "Reportar problema ou sugestão"}
        >
          {error ? <Bug size={24} /> : <HelpCircle size={24} />}
        </button>
      )}

      {/* Modal de report — bottom acima do botão "Especialista em Propostas" (bottom:84 + 50px de altura = topo em 134px), para abrir por cima dele em vez de atrás */}
      {effectiveIsOpen && (
        <div
          ref={panelRef}
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 12px 32px rgba(15,23,42,0.15)',
            width: `${panelWidth}px`,
            height: `${panelHeight}px`,
            position: 'fixed',
            bottom: !hasMoved && shiftLeft ? `${CHAT_PANEL_BOTTOM}px` : '150px',
            right: !hasMoved && shiftLeft ? `${CHAT_PANEL_RIGHT}px` : '20px',
            transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
            zIndex: 9998,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: hasMoved ? 'none' : 'right 0.2s ease, bottom 0.2s ease',
            animation: 'prainte-feedback-fadein 200ms ease',
          }}
        >
          <style>{`
            @keyframes prainte-feedback-fadein {
              from { opacity: 0; transform: translateY(8px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          {/* Alças de redimensionamento — mesma convenção do Especialista em Propostas */}
          <div onMouseDown={handleTopResize} style={{ position: 'absolute', top: 0, left: '16px', right: '16px', height: '6px', cursor: 'ns-resize', zIndex: 10 }} />
          <div onMouseDown={handleLeftResize} style={{ position: 'absolute', top: '16px', left: 0, bottom: '16px', width: '6px', cursor: 'ew-resize', zIndex: 10 }} />
          <div onMouseDown={handleCornerResize} style={{ position: 'absolute', top: 0, left: 0, width: '16px', height: '16px', cursor: 'nwse-resize', zIndex: 11, borderTopLeftRadius: '16px' }} />

          {/* Cabeçalho — mesmo verde do Especialista em Propostas, para os dois parecerem parte do mesmo módulo. Arrastável (fora dos botões) para mover o modal pela tela */}
          <div
            onMouseDown={handleDragStart}
            style={{
              background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
              padding: '12px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              flexShrink: 0,
              cursor: 'grab',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: '#fff' }}>
                <MessageSquare size={18} />
                {error ? 'Reportar Erro' : 'Feedback ou Suporte'}
              </h3>
              <button
                onClick={() => handleClose()}
                aria-label="Fechar"
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '6px',
                  display: 'flex',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.28)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Corpo — rolável, com escala de espaçamento compacta entre seções */}
          <div style={{ padding: '10px 24px', overflowY: 'auto', flex: 1 }}>
            {/* Tipo de report — segmented control (apenas se não for erro específico) */}
            {!error && (
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  Tipo de Feedback
                </label>
                <div style={{ display: 'flex', gap: '4px', backgroundColor: '#F3F4F6', borderRadius: '10px', padding: '4px' }}>
                  <button
                    onClick={() => setReportType('bug')}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      border: reportType === 'bug' ? '1px solid #22C55E' : '1px solid transparent',
                      cursor: 'pointer',
                      backgroundColor: reportType === 'bug' ? '#DCFCE7' : 'transparent',
                      color: reportType === 'bug' ? '#166534' : '#6B7280',
                      transition: 'all 180ms ease',
                    }}
                    onMouseEnter={(e) => { if (reportType !== 'bug') e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)'; }}
                    onMouseLeave={(e) => { if (reportType !== 'bug') e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Bug size={16} />
                    Bug
                  </button>
                  <button
                    onClick={() => setReportType('suggestion')}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      border: reportType === 'suggestion' ? '1px solid #22C55E' : '1px solid transparent',
                      cursor: 'pointer',
                      backgroundColor: reportType === 'suggestion' ? '#DCFCE7' : 'transparent',
                      color: reportType === 'suggestion' ? '#166534' : '#6B7280',
                      transition: 'all 180ms ease',
                    }}
                    onMouseEnter={(e) => { if (reportType !== 'suggestion') e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.04)'; }}
                    onMouseLeave={(e) => { if (reportType !== 'suggestion') e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Lightbulb size={16} />
                    Sugestão
                  </button>
                </div>
              </div>
            )}

            {/* Informações do erro (se existir) */}
            {error && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '10px',
                padding: '12px',
                marginBottom: '20px',
              }}>
                <p style={{ fontSize: '14px', fontWeight: '500', color: '#991b1b', margin: 0 }}>
                  {error.message}
                </p>
              </div>
            )}

            {/* Descrição do usuário */}
            <div style={{ marginBottom: '12px' }}>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#10B981';
                  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(16,185,129,0.15)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#D1D5DB';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff',
                  border: '1px solid #D1D5DB',
                  borderRadius: '12px',
                  padding: '12px',
                  fontSize: '14px',
                  resize: 'none',
                  fontFamily: 'inherit',
                  outline: 'none',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                }}
                rows={3}
                placeholder={error
                  ? "Descreva os passos que levaram ao erro..."
                  : "Descreva o problema ou sugestão..."
                }
              />
            </div>

            {/* Evidência — card com drag-and-drop */}
            <div style={{ marginBottom: '10px' }}>
              <div
                onDragOver={handleImageDragOver}
                onDragLeave={handleImageDragLeave}
                onDrop={handleImageDrop}
                style={{
                  backgroundColor: isDragOver ? '#ECFDF5' : '#F9FAFB',
                  border: `1px dashed ${isDragOver ? '#10B981' : '#E5E7EB'}`,
                  borderRadius: '12px',
                  padding: '10px',
                  transition: 'background-color 0.15s ease, border-color 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                  <ImagePlus size={16} color="#374151" />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Anexos (opcional)</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={captureScreenshot}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      backgroundColor: '#E5E7EB',
                      color: '#374151',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease, transform 0.15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D1D5DB'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#E5E7EB'; e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    <Camera size={16} />
                    Capturar tela
                  </button>

                  <label
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      backgroundColor: '#E5E7EB',
                      color: '#374151',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease, transform 0.15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D1D5DB'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#E5E7EB'; e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <Upload size={16} />
                    Escolher imagens
                  </label>

                  <label
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      backgroundColor: '#E5E7EB',
                      color: '#374151',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease, transform 0.15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D1D5DB'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#E5E7EB'; e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    <input
                      type="file"
                      accept={DOCUMENT_EXTENSIONS}
                      multiple
                      onChange={handleDocumentUpload}
                      style={{ display: 'none' }}
                    />
                    <Paperclip size={16} />
                    Anexar documento
                  </label>
                </div>

                {attachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                    {attachments.map((att, idx) => (
                      <div key={idx} style={{ position: 'relative', width: 'calc(50% - 4px)' }}>
                        {att.isImage ? (
                          <img
                            src={att.dataUrl}
                            alt={att.name}
                            style={{
                              width: '100%',
                              height: '100px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '1px solid #d1d5db',
                              backgroundColor: '#f9fafb'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            backgroundColor: '#f9fafb',
                            padding: '8px',
                          }}>
                            <FileText size={24} color="#6B7280" />
                            <p style={{
                              fontSize: '11px',
                              color: '#374151',
                              margin: 0,
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {att.name}
                            </p>
                          </div>
                        )}
                        <button
                          onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: '#EF4444',
                            color: 'white',
                            borderRadius: '50%',
                            padding: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DC2626'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EF4444'}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    {attachments.length < MAX_ATTACHMENTS && (
                      <p style={{ width: '100%', fontSize: '11px', color: '#9ca3af', margin: 0 }}>
                        {attachments.length}/{MAX_ATTACHMENTS} anexos
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Botão de ação — único, primário */}
            <button
              onClick={submitToTrello}
              disabled={isSubmitting || !description.trim()}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: (isSubmitting || !description.trim()) ? '#9ca3af' : '#10B981',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 600,
                border: 'none',
                cursor: (isSubmitting || !description.trim()) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'background-color 0.15s ease, transform 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (isSubmitting || !description.trim()) return;
                e.currentTarget.style.backgroundColor = '#059669';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = (isSubmitting || !description.trim()) ? '#9ca3af' : '#10B981';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {isSubmitting ? (
                'Enviando...'
              ) : (
                <>
                  <Send size={16} />
                  Enviar
                </>
              )}
            </button>

            {/* Suporte — mesmos canais e ícones já usados em Dashboard.tsx e ProposalDetails/index.tsx */}
            <div style={{ borderTop: '1px solid #E5E7EB', marginTop: '12px', paddingTop: '10px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6B7280', textAlign: 'center' }}>
                💬 Precisa falar com alguém?
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => window.open('https://api.whatsapp.com/send/?phone=48992157605', '_blank')}
                  style={{
                    flex: 1,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    backgroundColor: '#F0FDF4',
                    border: '1px solid #BBF7D0',
                    color: '#166534',
                    padding: '8px 12px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease, transform 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#DCFCE7'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F0FDF4'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#25D366', flexShrink: 0 }}>
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.13 1.588 5.931L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Contatar
                </button>

                <button
                  onClick={() => window.open('https://calendly.com/ademar-schmitz-wisswelt/suporte-prainte-problemas-tecnicos', '_blank')}
                  style={{
                    flex: 1,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    backgroundColor: '#EFF6FF',
                    border: '1px solid #BFDBFE',
                    color: '#1E40AF',
                    padding: '8px 12px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease, transform 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#DBEAFE'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#EFF6FF'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#0066FF', flexShrink: 0 }}>
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  Agendar Horário
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
